/**
 * treatmentRecommender
 * --------------------
 * Given the current conditions on a tooth (with optional per-condition
 * surfaces), return an ordered list of recommended procedures with a tier
 * badge, star rating and clinician-friendly reason.
 *
 * The engine intentionally reasons across **multiple diagnoses together**
 * (e.g. Caries + Wear ⇒ Crown moves up alongside Composite; Missing +
 * Mobility ⇒ Extraction + Implant sequence). It never asks the doctor to
 * re-enter clinical findings that already live on the chart.
 *
 * Shape of each recommendation:
 *   {
 *     procedure: "Filling",
 *     variant: "Composite" | null,
 *     tier: "recommended" | "alternative" | "conditional" | "long_term",
 *     stars: 1..5,
 *     reason: "Restores active caries and lost tooth structure.",
 *   }
 *
 * Procedure/variant strings match Viyora's seed procedure catalog so the
 * chart → Treatments handoff finds a catalog row automatically.
 */

const TIER_ORDER = { recommended: 0, alternative: 1, long_term: 2, conditional: 3 };

export const TIER_LABEL = {
  recommended: "Recommended",
  alternative: "Alternative",
  long_term:   "Long-term",
  conditional: "If indicated",
};

export function recommendTreatments(conditions = []) {
  const codes = new Set(
    (conditions || [])
      .map((c) => (typeof c === "string" ? c : c?.code))
      .filter(Boolean)
  );
  const has = (c) => codes.has(c);
  const anyRestorative = has("caries") || has("fracture") ||
                         has("wear") || has("attrition") || has("hypoplasia");

  const out = [];
  const push = (r) => out.push(r);

  // Human-readable finding summary — used in reasons.
  const findings = [];
  if (has("caries"))     findings.push("active caries");
  if (has("fracture"))   findings.push("coronal fracture");
  if (has("wear"))       findings.push("wear");
  if (has("attrition"))  findings.push("attrition");
  if (has("hypoplasia")) findings.push("enamel hypoplasia");
  const findingText = findings.join(" + ");

  // ---- Restorative pathway --------------------------------------------
  if (anyRestorative) {
    push({
      procedure: "Filling", variant: "Composite",
      tier: "recommended", stars: 5,
      reason: findingText
        ? `Treats ${findingText}. Least invasive restorative option.`
        : "Least invasive direct restoration.",
    });
    if (has("caries")) {
      push({
        procedure: "Filling", variant: "GIC",
        tier: "alternative", stars: 4,
        reason: "Fluoride-releasing option — use when moisture control is difficult.",
      });
    }
    if (has("caries") && !has("wear") && !has("attrition")) {
      push({
        procedure: "Filling", variant: "Amalgam",
        tier: "alternative", stars: 3,
        reason: "Durable stress-bearing restoration for posterior teeth.",
      });
    }
    if (has("wear") || has("attrition") || has("fracture")) {
      const wearWord =
        has("wear") && has("attrition") ? "severe wear + attrition"
        : has("wear")                    ? "wear"
        : has("attrition")               ? "attrition"
        : "coronal fracture";
      push({
        procedure: "Crown", variant: "PFM",
        tier: "long_term", stars: 4,
        reason: `Full-coverage crown recommended because tooth also has ${wearWord}.`,
      });
    }
    if (has("caries") || has("fracture")) {
      push({
        procedure: "Root Canal",
        tier: "conditional", stars: 3,
        reason: "Only if pulpal involvement is suspected on radiograph or symptomatology.",
      });
    }
    if (has("caries") && has("fracture")) {
      push({
        procedure: "Extraction", variant: "Simple",
        tier: "conditional", stars: 2,
        reason: "Consider if remaining tooth structure is non-restorable.",
      });
    }
  }

  // ---- Endodontic ------------------------------------------------------
  if (has("periapical_lesion") || has("periapical")) {
    push({
      procedure: "Root Canal",
      tier: "recommended", stars: 5,
      reason: "Peri-apical lesion present — RCT is first-line therapy.",
    });
    push({
      procedure: "Extraction", variant: "Surgical",
      tier: "alternative", stars: 3,
      reason: "If root is non-restorable, plan surgical extraction + prosthetic replacement.",
    });
  }

  // ---- Missing / edentulous -------------------------------------------
  if (has("missing") || has("extraction") || has("extracted")) {
    push({
      procedure: "Implant (per tooth)",
      tier: "recommended", stars: 5,
      reason: "Gold-standard replacement — preserves adjacent tooth structure and bone.",
    });
    push({
      procedure: "Bridge (per unit)", variant: "PFM",
      tier: "alternative", stars: 4,
      reason: "Fixed alternative if adjacent teeth already need crowns.",
    });
    push({
      procedure: "Denture", variant: "Partial",
      tier: "long_term", stars: 3,
      reason: "Removable option for cost-sensitive or medically-compromised patients.",
    });
  }

  // ---- Mobility --------------------------------------------------------
  if (has("mobility")) {
    push({
      procedure: "Extraction", variant: "Simple",
      tier: "recommended", stars: 4,
      reason: "Grade II/III mobility with poor prognosis — extraction indicated.",
    });
  }

  // ---- Wear / Attrition only (no active decay) ------------------------
  if ((has("wear") || has("attrition")) && !has("caries") && !has("fracture")) {
    push({
      procedure: "Filling", variant: "Composite",
      tier: "recommended", stars: 4,
      reason: "Composite build-up restores lost tooth structure with minimal prep.",
    });
    push({
      procedure: "Crown", variant: "PFM",
      tier: "long_term", stars: 4,
      reason: "Full-coverage crown for severe wear / bruxism.",
    });
  }

  // ---- Hypoplasia (anterior aesthetics) --------------------------------
  if (has("hypoplasia")) {
    push({
      procedure: "Veneer", variant: "Porcelain",
      tier: "long_term", stars: 5,
      reason: "Best long-term aesthetic outcome for anterior enamel defects.",
    });
  }

  // ---- Deduplicate by (procedure, variant) keeping highest stars ------
  const bag = new Map();
  for (const r of out) {
    const key = `${r.procedure}::${r.variant || ""}`;
    const prev = bag.get(key);
    if (!prev || r.stars > prev.stars ||
        (r.stars === prev.stars && TIER_ORDER[r.tier] < TIER_ORDER[prev.tier])) {
      bag.set(key, r);
    }
  }
  return Array.from(bag.values()).sort((a, b) => {
    if (b.stars !== a.stars) return b.stars - a.stars;
    return TIER_ORDER[a.tier] - TIER_ORDER[b.tier];
  });
}

/** Renders the star rating as a short unicode string (used inline). */
export function stars(n) {
  const filled = "★".repeat(Math.max(0, Math.min(5, n)));
  const empty  = "☆".repeat(Math.max(0, 5 - Math.min(5, n)));
  return filled + empty;
}
